import { CreateUpdateConnectionRequest, CreateConnectionResponse, DataWarehouseObjectType, dataWarehouseObjectTypeToJSON, DataWarehouseType, SearchConnectionResponse } from "build-ts/thoughtspot";
import showToast from "../hooks/useCustomToast";

/**
 * Creates a new connection to a data warehouse.
 *
 * @param {CreateUpdateConnectionRequest} request - The connection request data.
 * @returns {Promise<ConnectionResponse>} A promise that resolves to the created connection.
 * @throws {Error} Throws an error if the request fails.
 */
export const createConnection = async (request: CreateUpdateConnectionRequest): Promise<CreateConnectionResponse | null> => {
  try {
    const response = await fetch(process.env.REACT_APP_CREATE_CONNECTION!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(CreateUpdateConnectionRequest.toJSON(request)),
    });

    if (!response.ok) {
      showToast("Error creating connection", "", "error");
      return null;
    }

    const data = await response.json();
    return CreateConnectionResponse.fromJSON(data);
  } catch (error) {
    showToast("Error creating connection", "", "error");
    return null;
  }
};

/**
 * Fetches the metadata of a connection.
 *
 * @param {string} connectionId - The ID of the connection.
 * @param {DataWarehouseType} dataWarehouseType - The type of data warehouse.
 * @param {DataWarehouseObjectType} dataWarehouseObjectType - The type of data warehouse object (default: COLUMN).
 * @param {boolean} includeDetails - Whether to include additional details (default: false).
 * @returns {Promise<any>} A promise that resolves to the connection metadata.
 */
export const getConnectionMetadata = async (
  connectionId: string,
  dataWarehouseType: DataWarehouseType,
  dataWarehouseObjectType: DataWarehouseObjectType = DataWarehouseObjectType.COLUMN,
  includeDetails: boolean = false
): Promise<SearchConnectionResponse | null> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_CONNECTION_METADATA}/${connectionId}?` +
      `dataWarehouseObjectType=${dataWarehouseObjectTypeToJSON(dataWarehouseObjectType)}`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Error fetching connection metadata", "", "error");
      return null;
    }

    const data = await response.json();
    return SearchConnectionResponse.fromJSON(data);
  } catch (error) {
    showToast("Error fetching connection metadata", "", "error");
    return null;
  }
};

/**
 * Updates an existing connection.
 *
 * @param {string} connectionId - The ID of the connection to update.
 * @param {CreateUpdateConnectionRequest} request - The updated connection data.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 */
export const updateConnection = async (
  connectionId: string,
  request: CreateUpdateConnectionRequest
): Promise<void> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_UPDATE_CONNECTION}/${connectionId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(CreateUpdateConnectionRequest.toJSON(request)),
    });

    if (!response.ok) {
      showToast("Error updating connection", "", "error");
      return;
    }

    showToast("Connection updated successfully", "", "success");
  } catch (error) {
    showToast("Error updating connection", "", "error");
  }
};

/**
 * Imports metadata for a connection and creates data models.
 *
 * @param {string} connectionId - The ID of the connection.
 * @returns {Promise<any>} A promise that resolves to the import response.
 */
export const importConnectionMetadata = async (connectionId: string): Promise<any> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_IMPORT_CONNECTION_METADATA}/${connectionId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error importing connection metadata", "", "error");
      return null;
    }

    return await response.json();
  } catch (error) {
    showToast("Error importing connection metadata", "", "error");
    return null;
  }
}; 